import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useLabsEditor, BaseEditorState, EditorConfig } from './useLabsEditor';
import { useLabsHotkeys } from './useLabsHotkeys';
import { usePlaybookEditUI } from './useLabsUI';
import { PlaybookEditState } from '@/pages/labs/PlaybookEditPage';
import { PlaybookTransformType, PlaybookTransformItem } from '@/types/playbook';
import { getPlaybook, updatePlaybook, createPlaybook } from '@/services/playbookApiService';
import { EditorSelection } from '@components/playbooks/PlaybookEditorTab';

// Extend PlaybookEditState to implement BaseEditorState
interface ExtendedPlaybookEditState extends Omit<PlaybookEditState, 'status' | 'version'>, BaseEditorState {
  // Override status to match BaseEditorState
  status: 'saved' | 'draft' | 'published';
  // Keep version from PlaybookEditState (string)
  version: string;
}

// Validation function for playbook state
const validatePlaybookState = (state: ExtendedPlaybookEditState) => {
  const warnings: string[] = [];
  
  if (!state.name.trim()) {
    warnings.push('Playbook name is required');
  }
  
  if (!state.input_schema_id) {
    warnings.push('Input schema is required');
  }
  
  if (!state.output_schema_id) {
    warnings.push('Output schema is required');
  }
  
  return {
    isValid: warnings.length === 0,
    warnings,
  };
};

// Load function for playbooks
const loadPlaybook = async (id: string): Promise<ExtendedPlaybookEditState | null> => {
  try {
    const fetchedPlaybook = await getPlaybook(id);
    if (!fetchedPlaybook) return null;

    const playbookContent = (fetchedPlaybook as any).content;
    let parsedData: any = {};

    if (typeof playbookContent === 'string' && playbookContent.trim()) {
      // Parse YAML content if needed
      // parsedData = parsePlaybookFromYaml(playbookContent);
    }

    return {
      playbookId: fetchedPlaybook.id,
      name: fetchedPlaybook.name,
      description: fetchedPlaybook.description || '',
      type: fetchedPlaybook.type,
      version: fetchedPlaybook.version,
      params: fetchedPlaybook.params || [],
      input_schema_id: fetchedPlaybook.input_schema_id,
      output_schema_id: fetchedPlaybook.output_schema_id,
      uiTransforms: (fetchedPlaybook as any).uiTransforms || parsedData.uiTransforms || [],
      uiEventConfigs: (fetchedPlaybook as any).uiEventConfigs || parsedData.uiEventConfigs || [],
      autoPromoteGlobals: (fetchedPlaybook as any).autoPromoteGlobals ?? fetchedPlaybook.autoPromoteGlobals ?? true,
      allowUndefinedTransforms: (fetchedPlaybook as any).allowUndefinedTransforms ?? false,
      connectionJSON: (fetchedPlaybook as any).connectionJSON || null,
      isNewPlaybook: false,
      isSubmitting: false,
      baselineSchema: (fetchedPlaybook as any).baselineSchema || undefined,
      __originalYaml: parsedData.__originalYaml || undefined,
      id: fetchedPlaybook.id,
      content: fetchedPlaybook.content,
      status: fetchedPlaybook.status,
      activeTab: 'details',
      // BaseEditorState properties
      isNew: false,
      // Map database updated_at timestamp to lastSaved for conflict dialog
      lastSaved: (fetchedPlaybook as any).updated_at ? new Date((fetchedPlaybook as any).updated_at).getTime() : undefined,
    } as ExtendedPlaybookEditState;
  } catch (error) {
    console.error('Failed to load playbook:', error);
    throw error;
  }
};

// Save function for playbooks
const savePlaybook = async (state: ExtendedPlaybookEditState): Promise<ExtendedPlaybookEditState> => {
  const payload = {
    id: state.playbookId,
    name: state.name,
    description: state.description,
    type: state.type,
    version: state.version,
    params: state.params,
    input_schema_id: state.input_schema_id,
    output_schema_id: state.output_schema_id,
    content: {
      uiTransforms: state.uiTransforms,
      uiEventConfigs: state.uiEventConfigs,
      autoPromoteGlobals: state.autoPromoteGlobals,
      allowUndefinedTransforms: state.allowUndefinedTransforms,
      connectionJSON: state.connectionJSON
    },
    status: state.status,
    uiTransforms: state.uiTransforms,
    uiEventConfigs: state.uiEventConfigs,
    autoPromoteGlobals: state.autoPromoteGlobals,
    allowUndefinedTransforms: state.allowUndefinedTransforms,
    connectionJSON: state.connectionJSON
  };

  let result;
  if (state.isNewPlaybook) {
    result = await createPlaybook(payload as any);
  } else {
    result = await updatePlaybook(state.playbookId!, payload as any);
  }

  return {
    ...state,
    playbookId: result.id,
    id: result.id,
    isNewPlaybook: false,
    isNew: false,
    // Map the backend updated_at timestamp to lastSaved
    lastSaved: (result as any).updated_at ? new Date((result as any).updated_at).getTime() : Date.now(),
  };
};

export function usePlaybookEditor() {
  const navigate = useNavigate();
  const { playbookId, tab } = useParams<{ playbookId: string; tab?: string }>();
  const location = useLocation();
  
  // Determine playbook type from URL path
  const playbookType = location.pathname.includes('/labs/playbooks/') ? 'pipeline' : 'integration';
  
  // Create initial state
  const initialState = useMemo((): ExtendedPlaybookEditState => {
    const isNew = playbookId === 'new' || playbookId === undefined;
    const currentTab = tab || 'details';
    
    return {
      playbookId: isNew ? '' : playbookId || '',
      name: '',
      description: '',
      type: (playbookType as any) || 'pipeline',
      version: '1.0.0',
      params: [],
      input_schema_id: undefined,
      output_schema_id: undefined,
      uiTransforms: [],
      uiEventConfigs: [],
      autoPromoteGlobals: true,
      allowUndefinedTransforms: false,
      isNewPlaybook: isNew,
      isSubmitting: false,
      baselineSchema: undefined,
      id: isNew ? 'new' : playbookId || '',
      content: {},
      status: 'saved',
      connectionJSON: null,
      activeTab: currentTab,
      // BaseEditorState properties
      isNew,
    };
  }, [playbookId, playbookType, tab]);

  // Editor configuration
  const config: EditorConfig<ExtendedPlaybookEditState> = useMemo(() => ({
    editorType: 'playbook',
    storageKeyPrefix: 'labs-playbook-editor',
    maxHistory: 50,
    enableHotkeys: true,
    enableUndoRedo: true,
    validateState: validatePlaybookState,
    onSave: savePlaybook,
    onLoad: loadPlaybook,
  }), []);

  // Use the base editor hook
  const editor = useLabsEditor(initialState, config);

  // UI state management
  const { uiState: labsUIState, updateUIState: updateLabsUIState } = usePlaybookEditUI(playbookId);

  // UI state getters and setters
  const currentSchemaView = labsUIState.currentSchemaView;
  const setCurrentSchemaView = useCallback((view: 'input' | 'output' | 'yaml' | ((prev: 'input' | 'output' | 'yaml') => 'input' | 'output' | 'yaml')) => {
    const newView = typeof view === 'function' ? view(labsUIState.currentSchemaView) : view;
    updateLabsUIState({ currentSchemaView: newView });
  }, [updateLabsUIState, labsUIState.currentSchemaView]);
  
  const currentYamlView = labsUIState.currentYamlView;
  const setCurrentYamlView = useCallback((view: 'playbook' | 'connection' | ((prev: 'playbook' | 'connection') => 'playbook' | 'connection')) => {
    const newView = typeof view === 'function' ? view(labsUIState.currentYamlView) : view;
    updateLabsUIState({ currentYamlView: newView });
  }, [updateLabsUIState, labsUIState.currentYamlView]);
  
  const selection = labsUIState.selection;
  const setSelection = useCallback((newSelection: EditorSelection | ((prev: EditorSelection) => EditorSelection)) => {
    const selection = typeof newSelection === 'function' ? newSelection(labsUIState.selection) : newSelection;
    updateLabsUIState({ selection });
  }, [updateLabsUIState, labsUIState.selection]);

  // Playbook-specific actions
  const handleAddTransforms = useCallback((newTransforms: PlaybookTransformItem[]) => {
    editor.updateState((prevState: ExtendedPlaybookEditState) => {
      const updatedTransforms = [...prevState.uiTransforms, ...newTransforms];
      const newSelection: EditorSelection = newTransforms.length > 0
        ? { type: 'global', transformId: newTransforms[0].id }
        : selection;

      setSelection(newSelection);
      return { ...prevState, uiTransforms: updatedTransforms };
    });
  }, [editor.updateState, setSelection, selection]);

  const handleAddTransformHotkey = useCallback((type: PlaybookTransformType) => {
    const newTransform: PlaybookTransformItem = {
      id: `transform_${Date.now()}`,
      type: type,
      content: {},
      appliesToScope: 'global',
      eventIds: undefined,
      isNew: true,
    };
    handleAddTransforms([newTransform]);
  }, [handleAddTransforms]);

  // Hotkey handlers
  const handleOpenInputSchema = useCallback(() => {
    setCurrentSchemaView('input');
  }, [setCurrentSchemaView]);

  const handleOpenOutputSchema = useCallback(() => {
    setCurrentSchemaView('output');
  }, [setCurrentSchemaView]);

  const handleOpenYamlPreview = useCallback(() => {
    setCurrentSchemaView('yaml');
  }, [setCurrentSchemaView]);

  const handleSelectConnectionSection = useCallback(() => {
    setSelection({ type: 'connection' });
  }, [setSelection]);

  const handleSelectGlobalSection = useCallback(() => {
    setSelection({ type: 'global' });
  }, [setSelection]);

  // Setup playbook-specific hotkeys
  useLabsHotkeys(
    editor.undo,
    editor.redo,
    handleOpenInputSchema,
    handleOpenOutputSchema,
    handleOpenYamlPreview,
    handleAddTransformHotkey,
    handleSelectConnectionSection,
    handleSelectGlobalSection
  );

  // Navigation helpers
  const navigateToList = useCallback(() => {
    const basePath = playbookType === 'pipeline' ? '/labs/playbooks' : '/labs/integrations';
    navigate(basePath);
  }, [navigate, playbookType]);

  const navigateToEdit = useCallback((id: string, tabName?: string) => {
    const basePath = playbookType === 'pipeline' ? '/labs/playbooks' : '/labs/integrations';
    const tabPath = tabName ? `/edit/${tabName}` : '/edit/details';
    navigate(`${basePath}/${id}${tabPath}`);
  }, [navigate, playbookType]);

  return {
    // Core editor functionality
    ...editor,
    
    // Playbook-specific state
    playbookState: editor.state,
    updatePlaybookState: editor.updateState,
    
    // UI state
    currentSchemaView,
    setCurrentSchemaView,
    currentYamlView,
    setCurrentYamlView,
    selection,
    setSelection,
    labsUIState,
    updateLabsUIState,
    
    // Playbook-specific actions
    handleAddTransforms,
    handleAddTransformHotkey,
    
    // Navigation
    navigateToList,
    navigateToEdit,
    
    // URL params
    playbookId,
    tab,
    playbookType,
  };
}
